import type { DashboardData, GccCombinedSource, Project, Role, Subfunction } from '../types'
import type { GccCombinableScenario, GccDashboardData, GccOutsourcingScenario } from './types'

/**
 * Fraction of GCC-eligible headcount that moves offshore per scenario. The
 * rest stays onshore as a residual. Retained has no residual — it is the
 * HQ + Markets population on its own.
 */
export const GCC_SCENARIO_OFFSHORE_RATE: Record<GccOutsourcingScenario, number> = {
  High: 1.0,
  Medium: 0.8,
  Low: 0.6,
  Retained: 1.0,
}

export const GCC_COMBINABLE_SCENARIOS: readonly GccCombinableScenario[] = ['Medium', 'Low']

export function isGccCombinableScenario(s: unknown): s is GccCombinableScenario {
  return s === 'Medium' || s === 'Low'
}

export function combinedDashboardLabel(scenario: GccCombinableScenario): string {
  return `Combined: Retained + ${scenario}`
}

/** Display label for the residual segment of a combined dashboard. */
export const residualSegmentLabel = (scenario: GccCombinableScenario): string =>
  `GCC-eligible residual (${scenario})`

export const RETAINED_SEGMENT_LABEL = 'Retained (HQ + Markets)'

/** Section titles for the two branches in a combined dashboard's Workforce Flow tab. */
export const GCC_WORKFORCE_LABEL = 'GCC Workforce'
export const RETAINED_WORKFORCE_LABEL = 'Retained Organization Workforce'

export const normalizeRoleName = (name: string): string => name.trim().replace(/\s+/g, ' ').toLowerCase()

const toNumber = (value: unknown): number => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

const roleList = (roles: unknown): Array<{ roleName: string; headcountEndState: number }> =>
  Array.isArray(roles)
    ? roles
        .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && typeof r.roleName === 'string')
        .map((r) => ({ roleName: r.roleName as string, headcountEndState: toNumber(r.headcountEndState) }))
    : []

/**
 * Residual end-state headcount per (normalized) role name: the GCC-eligible
 * roles that stay onshore under the scenario. Same rounding as the TN seed.
 */
export function computeResidualHeadcounts(
  gccData: GccDashboardData,
  scenario: GccOutsourcingScenario,
): Map<string, number> {
  const residualRate = 1 - GCC_SCENARIO_OFFSHORE_RATE[scenario]
  const result = new Map<string, number>()
  if (residualRate <= 0) return result
  for (const role of roleList(gccData.gccPopulation?.endStateRoles)) {
    const hc = Math.round(role.headcountEndState * residualRate)
    if (hc <= 0) continue
    const key = normalizeRoleName(role.roleName)
    result.set(key, (result.get(key) ?? 0) + hc)
  }
  return result
}

export interface CombinedDashboardSummary {
  retainedFte: number
  residualFte: number
  totalFte: number
  /** Scenario-analysis roles dropped because the Retained analysis already covers them. */
  droppedRetainedRoles: string[]
  /** Scenario-analysis roles matching neither population — usually renamed by the AI. */
  unmatchedRoles: string[]
}

export interface CombinedDashboardResult {
  data: DashboardData
  summary: CombinedDashboardSummary
}

const sumFtes = (subfunctions: Subfunction[]): number =>
  subfunctions.reduce(
    (total, sf) => total + Object.values(sf.roles ?? {}).reduce((t, r) => t + toNumber(r?.ftes), 0),
    0,
  )

/**
 * Freed FTEs, rounded per subfunction then summed: the same rule the dashboard's
 * KPI tiles use (computeKpiMetrics in @tn/core's template.ts), so the combined
 * narrative and the tiles show the same figure.
 */
const sumFreedFtes = (subfunctions: Subfunction[]): number =>
  subfunctions.reduce((total, sf) => {
    const freed = Object.values(sf.roles ?? {}).reduce((t, r) => {
      const ftes = toNumber(r?.ftes)
      const share = r?.capacity_freed_pct
      return ftes > 0 && typeof share === 'number' && Number.isFinite(share) ? t + (ftes * share) / 100 : t
    }, 0)
    return total + Math.round(freed)
  }, 0)

/**
 * Rescale a subfunction's archetype FTEs so they add up to its current FTEs
 * minus freed FTEs, the total the Workforce Flow rescales to (computeHrSummary
 * in @tn/core's template.ts). Keeps the ratios between archetypes and rounds
 * with floor + largest remainder, as normalizeArchetypeFTEs does, so the total
 * is exact. Without it a residual subfunction keeps archetypes sized for the
 * HQ + Markets roles dropped from it (WI 2816). Left as is when the target is
 * not positive or the archetypes add up to 0, as the Workforce Flow does.
 */
function fitArchetypesToFuture(sf: Subfunction): Subfunction {
  if (!sf.archetypes) return sf
  const target = sumFtes([sf]) - sumFreedFtes([sf])
  const entries = Object.entries(sf.archetypes)
  const total = entries.reduce((t, [, a]) => t + toNumber(a?.ftes), 0)
  if (target <= 0 || total <= 0 || total === target) return sf

  const raw = entries.map(([, a]) => (toNumber(a?.ftes) * target) / total)
  const ftes = raw.map((r) => Math.floor(r))
  let remainder = target - ftes.reduce((t, n) => t + n, 0)
  raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac)
    .forEach(({ i }) => {
      if (remainder-- > 0) ftes[i]++
    })
  return {
    ...sf,
    archetypes: Object.fromEntries(entries.map(([name, a], i) => [name, a ? { ...a, ftes: ftes[i] } : a])),
  }
}

interface BranchFigures {
  subfunctions: number
  roles: number
  tasks: number
  currentFte: number
  freedFte: number
  futureFte: number
}

const archetypesOf = (subfunctions: Subfunction[]) => subfunctions.flatMap((sf) => Object.values(sf.archetypes ?? {}))

/**
 * Same rules as the KPI tiles: future FTEs are the archetype FTEs when the
 * combined view has any archetypes, otherwise current minus freed. Every
 * branch follows the combined view's rule, so the branches add up to the tile.
 */
function branchFigures(subfunctions: Subfunction[], useArchetypes: boolean): BranchFigures {
  const currentFte = sumFtes(subfunctions)
  const freedFte = sumFreedFtes(subfunctions)
  const roles = subfunctions.flatMap((sf) => Object.values(sf.roles ?? {}))
  return {
    subfunctions: subfunctions.length,
    roles: roles.length,
    tasks: roles.reduce((t, r) => t + (r?.current_tasks ?? []).length, 0),
    currentFte,
    freedFte,
    futureFte: useArchetypes ? archetypesOf(subfunctions).reduce((t, a) => t + toNumber(a?.ftes), 0) : currentFte - freedFte,
  }
}

const fmt = (n: number): string => n.toLocaleString('en-US')
const count = (n: number, singular: string): string => `${fmt(n)} ${n === 1 ? singular : `${singular}s`}`
const fte = (n: number): string => `${fmt(n)} ${n === 1 ? 'FTE' : 'FTEs'}`
const freedPct = (b: BranchFigures): string => `${b.currentFte > 0 ? Math.round((b.freedFte / b.currentFte) * 100) : 0}%`

/**
 * The seven Intro journey cards for a combined dashboard, in card order. Worked
 * out from the merged data, so the template injects it without rewriting its
 * figures. The combined % stays a token so it resolves to the tile's own value.
 */
function combinedJourneyNarrative(
  retained: BranchFigures,
  residual: BranchFigures,
  total: BranchFigures,
  scope: { builds: number; processAreas: number; movedToGcc: number; unmatchedRoles: number },
  scenario: GccCombinableScenario,
): string[] {
  const ret = RETAINED_SEGMENT_LABEL
  const res = 'the onshore GCC-eligible roles'
  const { builds, processAreas, movedToGcc, unmatchedRoles } = scope
  const support = `${count(builds, 'automation build')} and ${count(processAreas, 'process redesign area')}`
  return [
    `This combined view brings two branches together: the ${ret} analysis (${fte(retained.currentFte)}) and the ` +
      `GCC-eligible roles that stay onshore under the ${scenario} scenario (${fte(residual.currentFte)}). Together they cover ` +
      `${count(total.subfunctions, 'subfunction')}, ${count(total.roles, 'role')} and ${fte(total.currentFte)}.` +
      (movedToGcc > 0 ? ` A further ${fte(movedToGcc)} of GCC-eligible work moves to the GCC under this scenario and is outside this view.` : '') +
      ` HQ + Markets roles in the ${scenario} analysis are excluded because the ${ret} analysis already covers them, so no one is counted twice.` +
      (unmatchedRoles > 0
        ? ` ${count(unmatchedRoles, 'role')} in the ${scenario} analysis could not be matched to the GCC design and ${unmatchedRoles === 1 ? 'is' : 'are'} not included.`
        : ''),
    `Across both branches, ${count(total.tasks, 'task')} ${total.tasks === 1 ? 'was' : 'were'} broken down into the skills they need and how the work will be done: ` +
      `${fmt(retained.tasks)} in ${ret} and ${fmt(residual.tasks)} in ${res}.`,
    `The combined future-state workforce is ${fte(total.futureFte)}: ${fte(retained.futureFte)} in ${ret} and ` +
      `${fte(residual.futureFte)} in ${res}. It is supported by ${support}.`,
    `Leaders can decide on each branch separately. ${ret} moves from ${fte(retained.currentFte)} today to ` +
      `${fte(retained.futureFte)} in the future state, and ${res} move from ${fte(residual.currentFte)} to ${fte(residual.futureFte)}.`,
    `Together, the two branches free ${fte(total.freedFte)} of capacity, {CAPACITY_PCT} of today's combined effort: ` +
      `${fte(retained.freedFte)} (${freedPct(retained)}) in ${ret} and ${fte(residual.freedFte)} (${freedPct(residual)}) in ${res}.`,
    `Leaders choose how to use the ${fte(total.freedFte)} of freed capacity in each branch: redeploy it to strategic work or ` +
      `backlog, reinvest it in new and future roles, or release it.`,
    `The result is a combined onshore workforce of ${fte(total.futureFte)} across ${count(total.subfunctions, 'subfunction')}, ` +
      `supported by ${support}` +
      (movedToGcc > 0 ? `, with ${fte(movedToGcc)} of GCC-eligible work delivered from the GCC.` : '.'),
  ]
}

const dedupeByName = <T extends { name?: string }>(items: T[]): T[] => {
  const seen = new Set<string>()
  return items.filter((item) => {
    const key = normalizeRoleName(String(item.name ?? ''))
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const labelBlock = (paragraphs: string[] | undefined, label: string): string[] => {
  const list = (paragraphs ?? []).filter((p) => typeof p === 'string' && p.trim() !== '')
  if (list.length === 0) return []
  return [`${label} — ${list[0]}`, ...list.slice(1)]
}

/**
 * Roll a Retained (HQ + Markets) TN analysis up with the GCC-eligible residual
 * from a Medium or Low TN analysis. No AI call.
 *
 * Medium / Low analyses were seeded with the retained population *plus* the
 * residual, so their HQ + Markets roles are dropped here — the Retained
 * analysis is the source of truth for those. Only roles whose names match the
 * residual (`gccPopulation` roles that stay onshore) are kept, so no one is
 * counted twice. A role whose name exists in both populations is kept with
 * its FTE capped at the residual headcount. Every subfunction's archetype FTEs
 * are then rescaled to its current minus freed FTEs, so the Future Workforce
 * tile, the Intro narrative and the Workforce Flow show the same total.
 */
export function mergeCombinedDashboard(
  retained: DashboardData,
  offshore: DashboardData,
  gccData: GccDashboardData,
  scenario: GccCombinableScenario,
): CombinedDashboardResult {
  const residual = computeResidualHeadcounts(gccData, scenario)
  const retainedNames = new Set(roleList(gccData.retainedPopulation?.endStateRoles).map((r) => normalizeRoleName(r.roleName)))

  const retainedSubfunctions: Subfunction[] = (retained.subfunctions ?? []).map((sf) =>
    fitArchetypesToFuture({ ...sf, gccBranch: 'retained' }),
  )
  const takenNames = new Set(retainedSubfunctions.map((sf) => normalizeRoleName(sf.name)))
  const droppedRetainedRoles: string[] = []
  const unmatchedRoles: string[] = []

  const residualSubfunctions: Subfunction[] = []
  /** Scenario subfunction name → name it carries in the combined view. */
  const renamed = new Map<string, string>()
  for (const sf of offshore.subfunctions ?? []) {
    const keptRoles: Record<string, Role> = {}
    for (const [roleName, role] of Object.entries(sf.roles ?? {})) {
      const key = normalizeRoleName(roleName)
      const residualHc = residual.get(key)
      if (residualHc === undefined) {
        if (retainedNames.has(key)) droppedRetainedRoles.push(roleName)
        else unmatchedRoles.push(roleName)
        continue
      }
      keptRoles[roleName] = retainedNames.has(key)
        ? { ...role, ftes: Math.min(toNumber(role.ftes), residualHc) }
        : role
    }
    const keptKeys = new Set(Object.keys(keptRoles).map(normalizeRoleName))
    if (keptKeys.size === 0) continue

    // Keep archetypes that draw on a kept role (or name no source roles at all).
    const archetypes = sf.archetypes
      ? Object.fromEntries(
          Object.entries(sf.archetypes).filter(([, a]) => {
            const sources = a?.source_roles ?? []
            return sources.length === 0 || sources.some((s) => keptKeys.has(normalizeRoleName(String(s?.role ?? ''))))
          }),
        )
      : undefined

    let name = sf.name
    if (takenNames.has(normalizeRoleName(name))) name = `${sf.name} — ${residualSegmentLabel(scenario)}`
    takenNames.add(normalizeRoleName(name))
    residualSubfunctions.push(
      fitArchetypesToFuture({ ...sf, name, roles: keptRoles, ...(archetypes ? { archetypes } : {}), gccBranch: 'gcc' }),
    )
    renamed.set(sf.name, name)
  }

  // Scenario builds only count if they touch a residual subfunction we kept.
  const offshoreBuilds = (offshore.automation_builds ?? [])
    .map((b) => ({ ...b, subfunctions: (b.subfunctions ?? []).filter((s) => renamed.has(s)).map((s) => renamed.get(s) as string) }))
    .filter((b) => b.subfunctions.length > 0)

  const subfunctions = [...retainedSubfunctions, ...residualSubfunctions]
  const automationBuilds = dedupeByName([...(retained.automation_builds ?? []), ...offshoreBuilds])
  const processAreas = dedupeByName([...(retained.process_areas ?? []), ...(offshore.process_areas ?? [])])

  const useArchetypes = archetypesOf(subfunctions).length > 0
  const retainedFigures = branchFigures(retainedSubfunctions, useArchetypes)
  const residualFigures = branchFigures(residualSubfunctions, useArchetypes)
  const retainedFte = retainedFigures.currentFte
  const residualFte = residualFigures.currentFte
  const totalFte = retainedFte + residualFte

  // GCC-eligible end-state headcount that does not stay onshore, from the same design the residual comes from.
  const gccEligibleFte = roleList(gccData.gccPopulation?.endStateRoles).reduce((t, r) => t + r.headcountEndState, 0)
  const onshoreResidualFte = [...residual.values()].reduce((t, hc) => t + hc, 0)
  const movedToGcc = Math.max(0, Math.round(gccEligibleFte - onshoreResidualFte))

  const data: DashboardData = {
    subfunctions,
    automation_builds: automationBuilds,
    process_areas: processAreas,
    journey_narrative: combinedJourneyNarrative(
      retainedFigures,
      residualFigures,
      branchFigures(subfunctions, useArchetypes),
      { builds: automationBuilds.length, processAreas: processAreas.length, movedToGcc, unmatchedRoles: unmatchedRoles.length },
      scenario,
    ),
    closing_narrative: [
      ...labelBlock(retained.closing_narrative, RETAINED_SEGMENT_LABEL),
      ...labelBlock(offshore.closing_narrative, residualSegmentLabel(scenario)),
    ],
  }

  return {
    data,
    summary: { retainedFte, residualFte, totalFte, droppedRetainedRoles, unmatchedRoles },
  }
}

/**
 * True when either constituent changed (e.g. Request Changes) or disappeared
 * after the combined dashboard was built.
 */
export function isCombinedStale(
  source: GccCombinedSource,
  retained: Pick<Project, 'updatedAt'> | null | undefined,
  offshore: Pick<Project, 'updatedAt'> | null | undefined,
): boolean {
  if (!retained || !offshore) return true
  return retained.updatedAt !== source.sourceUpdatedAt.retained || offshore.updatedAt !== source.sourceUpdatedAt.offshore
}
